import {
  Purchases,
  type PurchasesOffering,
  type PurchasesOfferings,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';
import type {
  RevenueCatProvider,
  UnifiedOffering,
  UnifiedOfferings,
  UnifiedPackage,
} from './types';

const mapPackage = (pkg: PurchasesPackage): UnifiedPackage => ({
  identifier: pkg.identifier,
  priceString: pkg.product.priceString,
  productIdentifier: pkg.product.identifier,
  title: pkg.product.title,
  description: pkg.product.description,
  original: pkg,
});

const mapOffering = (offering: PurchasesOffering): UnifiedOffering => ({
  identifier: offering.identifier,
  serverDescription: offering.serverDescription,
  monthly: offering.monthly ? mapPackage(offering.monthly) : null,
  annual: offering.annual ? mapPackage(offering.annual) : null,
  availablePackages: offering.availablePackages.map(mapPackage),
});

const mapOfferings = (offerings: PurchasesOfferings): UnifiedOfferings => ({
  current: offerings.current ? mapOffering(offerings.current) : null,
  all: Object.keys(offerings.all).reduce(
    (acc, key) => {
      acc[key] = mapOffering(offerings.all[key]);
      return acc;
    },
    {} as Record<string, UnifiedOffering>,
  ),
});

export const RevenuecatMobileProvider: RevenueCatProvider = {
  // Initialize the revenuecat instance
  init: async ({ apiKey, appUserId }) => {
    await Purchases.configure({ apiKey, appUserID: appUserId });
  },

  // Get the customer info
  getCustomerInfo: async () => {
    return (await Purchases.getCustomerInfo()).customerInfo;
  },

  // Get offerings
  getOfferings: async () => {
    const offerings = await Purchases.getOfferings();
    return mapOfferings(offerings);
  },

  // Present the paywall
  presentPaywall: async () => {
    return await RevenueCatUI.presentPaywall({
      displayCloseButton: true,
    });
  },

  // Restore purchases
  restorePurchases: async () => {
    const customerInfo = await Purchases.restorePurchases();
    return customerInfo.customerInfo;
  },

  // Manage subscriptions
  manageSubscriptions: async () => {
    const url = (await Purchases.getCustomerInfo()).customerInfo.managementURL;

    if (!url) {
      throw new Error('No management URL found');
    }

    window.open(url, '_blank');
  },
};
